import { Suspense, useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './contexts/backend/AuthContext';
import { WebSocketProvider } from './contexts/backend/WebSocketContext';
import { ProductsProvider } from './contexts/backend/ProductsContext';
import { LanguageProvider } from './contexts/languages/LanguageContext';
import { CartProvider } from './contexts/CartContext';

import ProtectedLayout from './components/layout/ProtectedLayout';
import CartPanel from './components/global/payments/CartPanel';
import PurchaseModal from './components/global/payments/PurchaseModal';
import LoadingScreen from './components/global/LoadingScreen';
import { useCart } from './contexts/CartContext';
import { useAuth } from './contexts/backend/AuthContext';
import LoginModal from './components/landingpage/modals/LoginModal';
import lazyWithRetry from './utils/lazyWithRetry';
import Countdown from './pages/landingpage/Countdown';
import { isProduction } from './config/environment';
import { ApiTester } from './components/dev/ApiTester';

const Home = lazyWithRetry(() => import('./pages/landingpage/Home'));
const PartnerLanding = lazyWithRetry(() => import('./pages/landingpage/PartnerLanding'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/landingpage/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/landingpage/TermsOfService'));
const Certificates = lazyWithRetry(() => import('./pages/landingpage/Certificates'));
const ResetPasswordRedirect = lazyWithRetry(() => import('./pages/landingpage/ResetPasswordRedirect'));
const NewsletterConfirm = lazyWithRetry(() => import('./pages/landingpage/NewsletterConfirm'));
const NewsletterUnsubscribe = lazyWithRetry(() => import('./pages/landingpage/NewsletterUnsubscribe'));
const VerifyRegistration = lazyWithRetry(() => import('./pages/landingpage/VerifyRegistration'));
const VerifyPayment = lazyWithRetry(() => import('./pages/landingpage/VerifyPayment'));
const Affiliates = lazyWithRetry(() => import('./pages/landingpage/Affiliates'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));

const Overview = lazyWithRetry(() => import('./pages/dashboard/Overview'));
const AccountOverview = lazyWithRetry(() => import('./pages/dashboard/AccountOverview'));
const Payouts = lazyWithRetry(() => import('./pages/dashboard/Payouts'));
const Affiliate = lazyWithRetry(() => import('./pages/dashboard/Affiliate'));
const Support = lazyWithRetry(() => import('./pages/dashboard/Support'));
const Profile = lazyWithRetry(() => import('./pages/dashboard/Profile'));
const FundedAgreement = lazyWithRetry(() => import('./pages/dashboard/FundedAgreement'));

const queryClient = new QueryClient();

const COUNTDOWN_TARGET = new Date('2026-04-01T08:00:00Z').getTime();

function LoginRedirect() {
  const navigate = useNavigate();
  const { openLoginModal } = useAuth();
  useEffect(() => {
    navigate('/', { replace: true });
    openLoginModal();
  }, [navigate, openLoginModal]);
  return null;
}

function AppContent() {
  const { items, isPurchaseModalOpen, closePurchaseModal, clearCart, removeFromCart, updateQuantity } = useCart();
  const { isLoginModalOpen, closeLoginModal, isAuthenticated } = useAuth();
  const [countdownBypassed, setCountdownBypassed] = useState(
    () => sessionStorage.getItem('countdown_bypass') === 'true' || Date.now() >= COUNTDOWN_TARGET
  );
  const handleBypass = useCallback(() => setCountdownBypassed(true), []);

  const handleClose = () => {
    closePurchaseModal();
  };

  const handleSuccess = () => {
    clearCart();
  };

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
    if (items.length === 1) {
      closePurchaseModal();
    }
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    updateQuantity(itemId, quantity);
  };

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* === Public Routes === */}
          <Route path="/" element={countdownBypassed ? <Home /> : <Countdown onBypass={handleBypass} />} />
          <Route path="/partners/:partnername" element={<PartnerLanding />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/certificates/verify/:type/:id" element={<Certificates />} />
          <Route path="/certificates/*" element={<Certificates />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/reset-password/:token" element={<ResetPasswordRedirect />} />
          <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
          <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
          <Route path="/verify-registration/:token" element={<VerifyRegistration />} />
          <Route path="/verify-registration" element={<VerifyRegistration />} />
          <Route path="/verify/payment/:token" element={<VerifyPayment />} />
          <Route path="/affiliates" element={<Affiliates />} />
          <Route path="/login" element={<LoginRedirect />} />
          <Route path="/initiate-funded-contract" element={<Navigate to="/dashboard" replace />} />

          {/* === Protected Routes === */}
          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={<Overview />} />
            <Route path="/account-overview" element={<AccountOverview />} />
            <Route path="/payouts" element={<Payouts />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/support" element={<Support />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/funded-agreement" element={<FundedAgreement />} />
          </Route>

          {/* === Fallback === */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {/* Global Components */}
      <CartPanel />
      <PurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={handleClose}
        cartItems={items}
        onRemoveItem={handleRemoveItem}
        onUpdateQuantity={handleUpdateQuantity}
        onPurchaseSuccess={handleSuccess}
        onClearCart={clearCart}
      />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
      />

      {!isProduction() && isAuthenticated && <ApiTester />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ProductsProvider>
          <CartProvider>
            <Router>
              <AuthProvider>
                <WebSocketProvider>
                  <AppContent />
                </WebSocketProvider>
              </AuthProvider>
            </Router>
          </CartProvider>
        </ProductsProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
